'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();

import type { Control, FieldErrorsImpl, FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import { Checkbox as CheckboxUi } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea as TextAreaComponent } from '@/components/ui/textarea'
import type { Job } from '@/payload-types'
import React from 'react'

import { Error } from '../Error'

type OpenQuestion = NonNullable<Job['openQuestions']>[number]

interface OpenQuestionFieldProps {
  question: OpenQuestion
  register: UseFormRegister<FieldValues>
  control: Control
  errors: Partial<FieldErrorsImpl>
  setValue: UseFormSetValue<FieldValues>
}

export const OpenQuestionField: React.FC<OpenQuestionFieldProps> = ({
  question,
  register,
  control,
  errors,
  setValue,
}) => {
  const fieldName = `oq_${question.questionId}`
  // Job docs synced before `body` was de-localized still hold the old
  // per-locale shape ({ fr: '...' }). Rendering that object as a child
  // crashes the whole apply page, so coerce anything non-string away and
  // degrade to an unlabelled field until the next Recruitee sync.
  const asText = (value: unknown) => (typeof value === 'string' ? value : '')
  const label = asText(question.body)
  const required = question.required ?? false

  const renderLabel = (htmlFor: string) => (
    <Label htmlFor={htmlFor}>
      {label}
      {required && (
        <span className="required">
          * <span className="sr-only">(required)</span>
        </span>
      )}
    </Label>
  )

  switch (question.kind) {
    case 'string':
      return (
        <div>
          {renderLabel(fieldName)}
          <Input
            placeholder={label}
            id={fieldName}
            type="text"
            {...register(fieldName, { required })}
          />
          {errors[fieldName] && <Error />}
        </div>
      )

    case 'text':
      return (
        <div>
          {renderLabel(fieldName)}
          <TextAreaComponent
            id={fieldName}
            rows={5}
            {...register(fieldName, { required })}
          />
          {errors[fieldName] && <Error />}
        </div>
      )

    case 'boolean':
    case 'legal':
      return (
        <div>
          <div className="flex items-center gap-2">
            <CheckboxUi
              id={fieldName}
              {...register(fieldName, { required })}
              onCheckedChange={(checked) => {
                setValue(fieldName, checked)
              }}
            />
            <Label htmlFor={fieldName}>
              {required && (
                <span className="required">
                  * <span className="sr-only">(required)</span>
                </span>
              )}
              {label}
            </Label>
          </div>
          {errors[fieldName] && <Error />}
        </div>
      )

    case 'single_choice': {
      const options = [...(question.openQuestionOptions || [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      )
      return (
        <div>
          {renderLabel(fieldName)}
          <Controller
            control={control}
            defaultValue=""
            name={fieldName}
            render={({ field: { onChange, value } }) => (
              <SelectComponent onValueChange={onChange} value={value}>
                <SelectTrigger className="w-full" id={fieldName}>
                  <SelectValue placeholder={label} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt.optionId} value={String(opt.optionId)}>
                      {asText(opt.body)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectComponent>
            )}
            rules={{ required }}
          />
          {errors[fieldName] && <Error />}
        </div>
      )
    }

    case 'multi_choice': {
      const options = [...(question.openQuestionOptions || [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      )
      return (
        <div>
          {renderLabel(fieldName)}
          <Controller
            control={control}
            defaultValue={[]}
            name={fieldName}
            rules={{
              validate: required
                ? (v) => (Array.isArray(v) && v.length > 0) || 'This field is required'
                : undefined,
            }}
            render={({ field: { value, onChange } }) => {
              const selected: string[] = Array.isArray(value) ? value : []
              return (
                <div className="flex flex-col gap-2 mt-2">
                  {options.map((opt) => {
                    const optVal = String(opt.optionId)
                    return (
                      <div key={opt.optionId} className="flex items-center gap-2">
                        <CheckboxUi
                          id={`${fieldName}_${opt.optionId}`}
                          checked={selected.includes(optVal)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...selected, optVal]
                              : selected.filter((v) => v !== optVal)
                            onChange(next)
                          }}
                        />
                        <Label htmlFor={`${fieldName}_${opt.optionId}`}>
                          {asText(opt.body)}
                        </Label>
                      </div>
                    )
                  })}
                </div>
              )
            }}
          />
          {errors[fieldName] && <Error />}
        </div>
      )
    }

    case 'number':
      return (
        <div>
          {renderLabel(fieldName)}
          <Input
            id={fieldName}
            type="number"
            {...register(fieldName, { required })}
          />
          {errors[fieldName] && <Error />}
        </div>
      )

    case 'date':
      return (
        <div>
          {renderLabel(fieldName)}
          <Input
            id={fieldName}
            type="date"
            {...register(fieldName, { required })}
          />
          {errors[fieldName] && <Error />}
        </div>
      )

    case 'infobox':
      return (
        <div>
          <div dangerouslySetInnerHTML={{ __html: label }} />
        </div>
      )

    default:
      // Skip unsupported kinds (file, video, salary)
      return null
  }
}
